import React from 'react';
import BlogFigureControls from './BlogFigureControls';

type Snapshot = {
  level: number;
  marker: string;
  event: string;
  summaryLines?: number;
  decay?: boolean;
};

const rowColors = [
  '#35a7c8',
  '#ef8354',
  '#55a868',
  '#8d72c7',
  '#e7ad2f',
  '#20a58e',
  '#d86f9b',
  '#5f83d1',
  '#c59632',
  '#3e9f78'
];

const summaryGradients = [
  `linear-gradient(90deg, ${rowColors[0]} 0 12%, ${rowColors[3]} 12% 27%, ${rowColors[1]} 27% 41%, ${rowColors[6]} 41% 55%, ${rowColors[4]} 55% 72%, ${rowColors[8]} 72% 86%, ${rowColors[2]} 86% 100%)`,
  `linear-gradient(90deg, ${rowColors[7]} 0 14%, ${rowColors[2]} 14% 31%, ${rowColors[9]} 31% 45%, ${rowColors[5]} 45% 62%, ${rowColors[0]} 62% 78%, ${rowColors[3]} 78% 89%, ${rowColors[1]} 89% 100%)`
];

const regular: Snapshot[] = [
  { level: 2, marker: '20%', event: 'context added' },
  { level: 4, marker: '40%', event: 'context added' },
  { level: 6, marker: '60%', event: 'context added' },
  { level: 8, marker: '80%', event: 'context added' },
  { level: 10, marker: '100%', event: 'window full' },
  { level: 2, marker: '10%', event: 'whole context summarized', summaryLines: 2 },
  { level: 4, marker: '30%', event: 'context added again', summaryLines: 2 }
];

const piper: Snapshot[] = [
  { level: 2, marker: '20%', event: 'context added' },
  { level: 4, marker: '40%', event: 'context added' },
  { level: 6, marker: '60%', event: 'context added' },
  { level: 8, marker: '80%', event: 'working ceiling reached' },
  { level: 8, marker: '80%', event: 'age-weighted compaction', decay: true },
  { level: 8, marker: '80%', event: 'age-weighted compaction', decay: true },
  { level: 8, marker: '80%', event: 'working set maintained', decay: true }
];

const ContextPanel: React.FC<{
  label: string;
  snapshot: Snapshot;
  active?: boolean;
  step: number;
}> = ({ label, snapshot, active = false, step }) => (
  <section className={`context-turn-panel${active ? ' context-turn-panel--active' : ''}`}>
    <header>
      <span>{label}</span>
      <strong>{snapshot.marker}</strong>
    </header>
    <div className="context-turn-panel__window" aria-hidden="true">
      {active && <i className="context-turn-panel__ceiling" />}
      <div className="context-turn-panel__lines" key={`${label}-${step}`}>
        {Array.from({ length: snapshot.level }).map((_, lineIndex) => {
          // flex-direction is column-reverse: index 0 is the newest layer at
          // the bottom. Preserve the newest two layers, then reduce each
          // progressively older layer by another ten percent.
          const reduction = active && snapshot.decay
            ? Math.max(0, (lineIndex - 1) * 10)
            : 0;
          const compacted = reduction > 0;
          const summaryStart = snapshot.level - (snapshot.summaryLines ?? 0);
          const isSummary = lineIndex >= summaryStart;
          const summaryIndex = lineIndex - summaryStart;
          const lineColor = isSummary
            ? summaryGradients[summaryIndex % summaryGradients.length]
            : rowColors[lineIndex % rowColors.length];
          return (
            <i
              key={lineIndex}
              className={[
                compacted ? 'is-compacted' : '',
                isSummary ? 'is-summary' : ''
              ].filter(Boolean).join(' ')}
              style={{
                '--line-index': lineIndex,
                '--line-width': isSummary ? '90%' : `${92 - reduction}%`,
                '--line-opacity': Math.max(0.76, 1 - reduction / 240),
                '--line-color': lineColor,
                '--line-gray': compacted ? Math.min(0.38, reduction / 150) : 0
              } as React.CSSProperties}
            />
          );
        })}
      </div>
    </div>
    <p>{snapshot.event}</p>
  </section>
);

const PiperCompactionFigure: React.FC = () => {
  const [step, setStep] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);

  React.useEffect(() => {
    if (!playing) return;
    if (step === regular.length - 1) {
      setPlaying(false);
      return;
    }
    const timer = window.setTimeout(() => setStep((current) => current + 1), 1500);
    return () => window.clearTimeout(timer);
  }, [playing, step]);

  const previous = () => {
    setPlaying(false);
    setStep((current) => Math.max(0, current - 1));
  };

  const next = () => {
    setPlaying(false);
    setStep((current) => Math.min(regular.length - 1, current + 1));
  };

  const togglePlayback = () => {
    if (step === regular.length - 1) {
      setStep(0);
      setPlaying(true);
      return;
    }
    setPlaying((current) => !current);
  };

  return (
    <figure className="piper-compaction-figure">
      <div className="piper-compaction-figure__frame">
        <div className="piper-compaction-figure__turn">
          TURN {step + 1}
        </div>

        <div className="piper-compaction-figure__comparison">
          <ContextPanel label="REGULAR COMPACTION" snapshot={regular[step]} step={step} />
          <ContextPanel label="PIPER / ACTIVE" snapshot={piper[step]} active step={step} />
        </div>

        <div className="piper-compaction-figure__progress">
          <i style={{ width: `${((step + 1) / regular.length) * 100}%` }} />
        </div>
        <div className="piper-compaction-figure__count">{step + 1} / {regular.length}</div>

        <BlogFigureControls
          className="piper-compaction-figure__controls"
          playing={playing}
          onPrevious={previous}
          onToggle={togglePlayback}
          onNext={next}
          previousDisabled={step === 0}
          nextDisabled={step === regular.length - 1}
          label="Context compaction playback controls"
          itemLabel="turn"
        />
      </div>
      <figcaption>
        The same turns under regular compaction and Piper's continuously maintained working set.
      </figcaption>
    </figure>
  );
};

export default PiperCompactionFigure;
